import React, { useCallback, useState } from 'react';

import { cssMap as unboundedCssMap } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { Card } from '@atlaskit/smart-card/card/lazy';
import {
	ElementName,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
} from '@atlaskit/smart-card/enums';
import { MetadataBlock } from '@atlaskit/smart-card/metadata-block';
import { TitleBlock } from '@atlaskit/smart-card/title-block';
import { token } from '@atlaskit/tokens';

import { hasUrlResolved, markUrlResolved } from './resolved-url-cache';

const styles = cssMap({
	root: {
		position: 'relative',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	unresolved: {
		minHeight: '53px',
	},
	unresolvedSkeletonOverlay: {
		position: 'absolute',
		insetBlockStart: token('space.0'),
		insetBlockEnd: token('space.0'),
		insetInlineStart: token('space.0'),
		insetInlineEnd: token('space.0'),
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingLeft: token('space.100'),
		zIndex: 1,
	},
	skeletonRows: {
		flex: 1,
		minWidth: '0px',
	},
});

// These styles target Smart Card's internal DOM (icon, title link, element group), which requires
// nested selectors. `@atlaskit/css`'s `cssMap` disallows nested selectors, so we use
// `@compiled/react`'s `cssMap` here instead — the same approach used by the LinkList component in
// `@atlassian/smart-knowledge-cards`.
const unboundedStyles = unboundedCssMap({
	titleWrapper: {
		flex: 1,
		minWidth: '0px',
		overflow: 'hidden',
	},
	titleBlock: {
		flex: 1,
		minWidth: '0px',
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-element-group"]': {
			gap: token('space.0'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-smart-element="LinkIcon"]': {
			paddingTop: token('space.100'),
			paddingRight: token('space.100'),
			paddingBottom: token('space.100'),
			paddingLeft: token('space.100'),
			borderRadius: token('radius.large'),
			backgroundColor: token('color.background.neutral'),
			marginTop: token('space.negative.050'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'a[data-smart-element="Title"]': {
			'&:hover': {
				textDecoration: 'none',
			},
			'&:focus': {
				textDecoration: 'none',
			},
			'&:active': {
				textDecoration: 'none',
			},
		},
	},
	stateSlot: {
		flexShrink: 0,
		display: 'flex',
		justifyContent: 'flex-end',
		marginLeft: token('space.150'),
	},
});

interface RecentWorkItemProps {
	/** Smart-link URL rendered as a single recent-work row. */
	url: string;
	/** Zero-based position of this item within the list, used for analytics and test ids. */
	index: number;
	/** Fired when the row (smart link) is clicked. */
	onLinkClick?: (url: string, index: number) => void;
	testId?: string;
}

/**
 * A single borderless recent-work row.
 *
 * 1. Renders the URL as a borderless, individually-rounded Smart Card block link.
 * 2. Shows the link title, provider and modified metadata, plus any status badge.
 * 3. Overlays a loading skeleton until the Smart Card first resolves.
 */
export function RecentWorkItem({
	url,
	index,
	onLinkClick,
	testId,
}: RecentWorkItemProps): React.JSX.Element {
	const [resolved, setResolved] = useState(() => hasUrlResolved(url));

	const handleResolve = useCallback(() => {
		markUrlResolved(url);
		setResolved(true);
	}, [url]);

	return (
		<Box
			xcss={cx(styles.root, !resolved && styles.unresolved)}
			testId={testId ? `${testId}-item-${index}` : undefined}
		>
			<Card
				appearance="block"
				url={url}
				onClick={() => onLinkClick?.(url, index)}
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideBackground: true,
					hideElevation: true,
					hidePadding: true,
					size: SmartLinkSize.Medium,
					removeBlockRestriction: true,
				}}
				onResolve={handleResolve}
			>
				<Inline spread="space-between" alignBlock="center">
					<Inline alignBlock="center" xcss={unboundedStyles.titleWrapper}>
						<Box xcss={unboundedStyles.titleBlock}>
							<TitleBlock
								maxLines={1}
								showActionOnHover
								subtitle={[
									{ name: ElementName.Provider, hideIcon: true },
									{ name: ElementName.ModifiedOn },
								]}
								position={SmartLinkPosition.Center}
							/>
						</Box>
						<Box xcss={unboundedStyles.stateSlot}>
							<MetadataBlock maxLines={1} primary={[{ name: ElementName.State }]} />
						</Box>
					</Inline>
				</Inline>
			</Card>
			{!resolved && (
				<Box
					xcss={styles.unresolvedSkeletonOverlay}
					testId={testId ? `${testId}-item-${index}-skeleton` : undefined}
				>
					<Inline space="space.100" alignBlock="center">
						<Skeleton width="32px" height="32px" borderRadius={token('radius.small')} />
						<Stack xcss={styles.skeletonRows} space="space.100">
							<Skeleton width="40%" height="12px" borderRadius={token('radius.small')} />
							<Skeleton width="70%" height="12px" borderRadius={token('radius.small')} />
						</Stack>
					</Inline>
				</Box>
			)}
		</Box>
	);
}
